import type { ReactNode } from 'react'
import { BubbleMenu } from '@tiptap/react/menus'
import type { Editor } from '@tiptap/core'
import {
  Bold,
  Italic,
  Strikethrough,
  Code,
  List,
  ListOrdered,
  ListChecks,
  Link as LinkIcon,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { testIds } from '@/lib/test-ids'

interface TiptapFormattingToolbarProps {
  editor: Editor
}

const HEADING_LEVELS = [1, 2, 3, 4, 5] as const

function FormatButton({
  active,
  disabled,
  onClick,
  title,
  testId,
  className,
  children,
}: {
  active?: boolean
  disabled?: boolean
  onClick: () => void
  title: string
  testId: string
  className?: string
  children: ReactNode
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={cn(
        'h-7 w-7 shrink-0',
        active && 'bg-accent text-accent-foreground',
        className
      )}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      aria-pressed={active}
      data-testid={testId}
    >
      {children}
    </Button>
  )
}

export function TiptapFormattingToolbar({ editor }: TiptapFormattingToolbarProps) {
  const setLink = () => {
    const previous = editor.getAttributes('link').href as string | undefined
    const next = window.prompt('Link URL', previous ?? 'https://')
    if (next === null) return
    const trimmed = next.trim()
    if (trimmed === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: trimmed }).run()
  }

  return (
    <BubbleMenu
      editor={editor}
      options={{ placement: 'top', offset: 8 }}
      shouldShow={({ editor: ed, state }) => {
        if (!ed.isEditable) return false
        const { selection } = state
        if (selection.empty) return false
        return true
      }}
      className="flex items-center gap-0.5 rounded-md border bg-background/95 px-1 py-0.5 shadow-lg backdrop-blur-sm"
      data-testid={testIds.tiptapFormattingToolbar.root}
    >
      <FormatButton
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
        title="Bold (Cmd+B)"
        testId={testIds.tiptapFormattingToolbar.bold}
      >
        <Bold className="h-3.5 w-3.5" />
      </FormatButton>
      <FormatButton
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        title="Italic (Cmd+I)"
        testId={testIds.tiptapFormattingToolbar.italic}
      >
        <Italic className="h-3.5 w-3.5" />
      </FormatButton>
      <FormatButton
        active={editor.isActive('strike')}
        onClick={() => editor.chain().focus().toggleStrike().run()}
        title="Strikethrough"
        testId={testIds.tiptapFormattingToolbar.strike}
      >
        <Strikethrough className="h-3.5 w-3.5" />
      </FormatButton>
      <FormatButton
        active={editor.isActive('code')}
        onClick={() => editor.chain().focus().toggleCode().run()}
        title="Inline code"
        testId={testIds.tiptapFormattingToolbar.code}
      >
        <Code className="h-3.5 w-3.5" />
      </FormatButton>
      <FormatButton
        active={editor.isActive('link')}
        onClick={setLink}
        title="Link"
        testId={testIds.tiptapFormattingToolbar.link}
      >
        <LinkIcon className="h-3.5 w-3.5" />
      </FormatButton>

      <span className="mx-0.5 h-4 w-px bg-border" aria-hidden />

      {HEADING_LEVELS.map((level) => (
        <FormatButton
          key={level}
          active={editor.isActive('heading', { level })}
          onClick={() => editor.chain().focus().toggleHeading({ level }).run()}
          title={`Heading ${level}`}
          testId={testIds.tiptapFormattingToolbar.heading(level)}
          className="w-6 text-[10px] font-semibold"
        >
          H{level}
        </FormatButton>
      ))}

      <span className="mx-0.5 h-4 w-px bg-border" aria-hidden />

      <FormatButton
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        title="Bullet list"
        testId={testIds.tiptapFormattingToolbar.bulletList}
      >
        <List className="h-3.5 w-3.5" />
      </FormatButton>
      <FormatButton
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        title="Numbered list"
        testId={testIds.tiptapFormattingToolbar.orderedList}
      >
        <ListOrdered className="h-3.5 w-3.5" />
      </FormatButton>
      <FormatButton
        active={editor.isActive('taskList')}
        onClick={() => editor.chain().focus().toggleTaskList().run()}
        title="Task list"
        testId={testIds.tiptapFormattingToolbar.taskList}
      >
        <ListChecks className="h-3.5 w-3.5" />
      </FormatButton>
    </BubbleMenu>
  )
}
